import React from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Youtube, MessageCircle } from "lucide-react";
import { COMPANY, NAV_LINKS, buildWhatsAppUrl } from "@/lib/constants";
import Logo from "@/components/landing/Logo";

const SERVICES_LINKS = [
  "Outstation Cabs", "One Way Taxi", "Airport Pickup & Drop",
  "Self Drive Cars", "Corporate Travel", "Holiday Packages",
];

const SOCIALS = [
  { Icon: Facebook, label: "Facebook" },
  { Icon: Instagram, label: "Instagram" },
  { Icon: Twitter, label: "Twitter" },
  { Icon: Youtube, label: "YouTube" },
];

const Footer = () => {
  return (
    <footer data-testid="footer" className="relative bg-white border-t border-slate-200 pt-16 pb-8">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Logo heightClass="h-20" />
            <p className="mt-5 text-sm leading-relaxed text-[#64748B]">
              India's trusted cab booking partner for outstation trips, airport transfers, one-way drops and self-drive rentals.
            </p>
            <a href={buildWhatsAppUrl("Hi Cabsab.com, I'd like to book a cab.")} target="_blank" rel="noopener noreferrer"
              data-testid="footer-whatsapp-cta"
              className="mt-6 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-5 py-2.5 rounded-full transition-colors">
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>

          <div>
            <h4 className="font-display font-semibold text-[#0F172A] text-sm uppercase tracking-[0.18em]">Quick Links</h4>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l, i) => (
                <li key={`fl-${l.label}-${i}`}>
                  <a href={l.href} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm text-[#64748B] hover:text-[#19A8E8] transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-[#0F172A] text-sm uppercase tracking-[0.18em]">Services</h4>
            <ul className="mt-5 space-y-3">
              {SERVICES_LINKS.map((s) => (
                <li key={s}>
                  <a href="#services" className="text-sm text-[#64748B] hover:text-[#19A8E8] transition-colors"
                    data-testid={`footer-service-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-[#0F172A] text-sm uppercase tracking-[0.18em]">Reach Us</h4>
            <ul className="mt-5 space-y-4 text-sm">
              <li>
                <a href={`tel:${COMPANY.phoneTel}`} data-testid="footer-phone"
                  className="flex items-start gap-3 text-[#64748B] hover:text-[#19A8E8] transition-colors">
                  <Phone className="h-4 w-4 mt-0.5 text-[#19A8E8]" />
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-semibold">24/7</span>
                    {COMPANY.phoneDisplay}
                  </span>
                </a>
              </li>
              <li>
                <a href={`mailto:${COMPANY.email}`} data-testid="footer-email"
                  className="flex items-start gap-3 text-[#64748B] hover:text-[#19A8E8] transition-colors">
                  <Mail className="h-4 w-4 mt-0.5 text-[#19A8E8]" /> {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-[#64748B]">
                <MapPin className="h-4 w-4 mt-0.5 text-[#19A8E8]" />
                Serving Across 17 Cities · {COMPANY.address}
              </li>
            </ul>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map(({ Icon, label }) => (
                <a key={label} href="#" aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="h-9 w-9 rounded-full bg-[#F8FAFC] border border-slate-200 hover:bg-[#19A8E8] hover:border-[#19A8E8] flex items-center justify-center text-[#64748B] hover:text-white transition-all">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#64748B]">
          <div data-testid="footer-copyright">© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <a href="#" className="hover:text-[#19A8E8] transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-[#19A8E8] transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-[#19A8E8] transition-colors">Refund Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
